import React from 'react';
import './Skeleton.css';

/**
 * What a surface shows while its data is in flight.
 *
 * Every page used to render one line of text — "Loading delivery metrics…" — in place of the
 * whole screen, so the layout collapsed to a single row and then sprang back when the data
 * landed. A skeleton keeps the shape of what is coming, which removes that jump and makes
 * the wait legible: you can see a table is arriving rather than reading that something is.
 *
 * **The announcement is the point.** The bars are decoration and are hidden from assistive
 * technology; each loading region carries ONE polite status message instead, which is what
 * the text line was already doing. Losing that in the move to a visual placeholder is the
 * usual regression, so `label` is required on the region components rather than optional.
 */

type SkeletonProps = {
  /** `text` for a line of copy, `circle` for an avatar, `block` for a panel or chart. */
  variant?: 'text' | 'circle' | 'block';
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: React.CSSProperties;
};

export function Skeleton({
  variant = 'text', width, height, className = '', style,
}: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={`skeleton skeleton--${variant} ${className}`.trim()}
      style={{ width, height, ...style }}
    />
  );
}

/**
 * The wrapper every skeleton belongs in: it holds the one announcement for the region.
 *
 * `aria-busy` tells a screen reader the region is mid-update, and the visually hidden text
 * says what is loading. Without this the placeholders are silent and a non-sighted user gets
 * nothing at all where they used to get a sentence.
 */
export function SkeletonRegion({
  label, className = '', children,
}: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className} aria-busy="true">
      <span className="sr-only" role="status" aria-live="polite">{label}</span>
      {children}
    </div>
  );
}

/** A stand-in for a paragraph: `lines` bars, the last one short so it reads as prose. */
export function SkeletonText({ lines = 3, width }: { lines?: number; width?: number | string }) {
  return (
    <div className="skeleton-lines" style={{ width }}>
      {Array.from({ length: lines }, (_, i) => <Skeleton key={i} />)}
    </div>
  );
}

/**
 * A stand-in for a list or table: `rows` rows, each with an optional leading avatar.
 *
 * `columns` are relative widths, so a row can echo the real table's shape — a wide title
 * next to a couple of narrow cells — rather than being an undifferentiated grey band.
 */
export function SkeletonRows({
  label, rows = 5, columns = [3, 1, 1], avatar = false, className = '',
}: {
  label: string;
  rows?: number;
  columns?: number[];
  avatar?: boolean;
  className?: string;
}) {
  return (
    <SkeletonRegion label={label} className={className}>
      {Array.from({ length: rows }, (_, r) => (
        <div className="skeleton-row" key={r}>
          {avatar && <Skeleton variant="circle" width={28} height={28} />}
          {columns.map((flex, c) => (
            <Skeleton key={c} height={12} style={{ flex, minWidth: 0 }} />
          ))}
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** A stand-in for a grid of cards — the projects list, the documents grid. */
export function SkeletonCards({
  label, count = 6, className = '',
}: { label: string; count?: number; className?: string }) {
  return (
    <SkeletonRegion label={label} className={`skeleton-cards ${className}`.trim()}>
      {Array.from({ length: count }, (_, i) => (
        <div className="skeleton-card" key={i}>
          <Skeleton height={14} width="70%" />
          <Skeleton height={10} width="45%" />
          <Skeleton variant="block" height={8} style={{ marginTop: 14 }} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** A stand-in for a row of headline numbers — the metric tiles on Performance and Home. */
export function SkeletonTiles({
  label, count = 4, className = '',
}: { label: string; count?: number; className?: string }) {
  return (
    <SkeletonRegion label={label} className={`skeleton-tiles ${className}`.trim()}>
      {Array.from({ length: count }, (_, i) => (
        <div className="skeleton-card" key={i}>
          <Skeleton height={26} width="50%" />
          <Skeleton height={10} width="80%" style={{ marginTop: 10 }} />
        </div>
      ))}
    </SkeletonRegion>
  );
}
